import {
  Button,
  CheckboxFormField,
  Form,
  IFormRender,
  Label,
  TextareaFormField,
  TextFormField,
} from '@busybusy/webapp-react-ui';
import {
  OrganizationCustomFields,
  OrganizationCustomFieldsCreateInput,
  OrganizationCustomFieldsTypes,
  OrganizationCustomFieldsUpdateInput,
} from '__generated__/graphql';
import classNames from 'classnames';
import { HeaderDialog } from 'components';
import ButtonTray from 'components/foundation/ButtonTray/ButtonTray';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useOpenable } from 'hooks';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { extractCustomFields, ICustomFieldTableRow } from 'utils/organizationCustomFieldsUtils';

export interface ICreateEditCustomFieldDialogProps {
  className?: ClassName;
  selectedRow?: ICustomFieldTableRow | null;
  organizationCustomFields: OrganizationCustomFields | null;
  type: OrganizationCustomFieldsTypes;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (fieldIndex: number) => void;
}

export interface ICustomFieldFormData {
  label: string;
  secondaryLabel?: string;
  adminOnly: boolean;
}

const CreateEditCustomFieldDialog = (props: ICreateEditCustomFieldDialogProps) => {
  const { className, selectedRow, organizationCustomFields, type, isOpen, onClose, onDelete } = props;

  const { createOrganizationCustomFields, updateOrganizationCustomFields } = useOrganizationCustomFields();
  const title = t(`${selectedRow ? 'Edit' : 'Create'} Custom Field`);

  const deleteConfirmationDialog = useOpenable();

  const [fieldIndex, setFieldIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ICustomFieldFormData>({ label: '', secondaryLabel: '', adminOnly: false });

  useEffect(() => {
    if (organizationCustomFields) {
      const customFields = extractCustomFields(organizationCustomFields);

      if (selectedRow) {
        const index = customFields.findIndex((field) => field.title === selectedRow?.title);
        setFormData({
          label: selectedRow?.title,
          secondaryLabel: selectedRow?.subtitle,
          adminOnly: selectedRow?.adminOnly,
        });
        setFieldIndex(index);
      } else {
        const fieldKeys = ['title1', 'title2', 'title3', 'title4', 'title5'] as const;
        const firstNullIndex = fieldKeys.findIndex((key) => organizationCustomFields[key] == null);
        if (firstNullIndex !== -1) {
          setFieldIndex(firstNullIndex);
        }
      }
    }
  }, [organizationCustomFields, selectedRow]);

  const resetFields = () => {
    setFormData({ label: '', secondaryLabel: '', adminOnly: false });
    setFieldIndex(null);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleSubmit = (formData: ICustomFieldFormData) => {
    if (!isNil(organizationCustomFields)) {
      const fieldKeys = ['title1', 'title2', 'title3', 'title4', 'title5'] as const;
      const adminOnlyKeys = ['adminOnly1', 'adminOnly2', 'adminOnly3', 'adminOnly4', 'adminOnly5'] as const;
      const subtitleKeys = ['subtitle1', 'subtitle2', 'subtitle3', 'subtitle4', 'subtitle5'] as const;

      let input: OrganizationCustomFieldsUpdateInput = {
        id: organizationCustomFields.id,
      };

      input = {
        id: organizationCustomFields.id,
        ...fieldKeys.reduce((acc, key, index) => {
          acc[key] = fieldIndex === index ? formData.label : organizationCustomFields[key];
          acc[adminOnlyKeys[index]] =
            fieldIndex === index ? formData.adminOnly : organizationCustomFields[adminOnlyKeys[index]];
          acc[subtitleKeys[index]] =
            fieldIndex === index ? formData.secondaryLabel : organizationCustomFields[subtitleKeys[index]];
          return acc;
        }, {} as Partial<OrganizationCustomFieldsUpdateInput>),
      };

      updateOrganizationCustomFields(input).then(() => {
        handleClose();
      });
    } else {
      const input: OrganizationCustomFieldsCreateInput = {
        title1: formData.label,
        subtitle1: formData.secondaryLabel,
        adminOnly1: formData.adminOnly,
        createdOn: isoTimeStampUtc(),
        type,
      };
      createOrganizationCustomFields(input).then(() => {
        handleClose();
      });
    }
  };

  const handleDelete = () => {
    onDelete(fieldIndex!);
    handleClose();
    deleteConfirmationDialog.close();
  };
  const renderFormFields = (form: IFormRender<ICustomFieldFormData>) => (
    <div className="mx-8">
      <div className={'mt-5'}>
        <Label required={true}>{t('Label')}</Label>
        <TextFormField form={form} name={'label'} />
      </div>
      <div className={'my-5'}>
        <Label>{t('Secondary Label')}</Label>
        <h6>
          {t(
            'Optional text that appears below the label to give additional context or instructions to the person entering information into the field.'
          )}
        </h6>
        <TextareaFormField form={form} name={'secondaryLabel'} />
      </div>
      <div className={'mb-5'}>
        <CheckboxFormField
          name={'adminOnly'}
          form={form}
          label={
            <div className={'pl-3'}>
              <h5 className={'fc-1 fw-semi-bold'}>{t('Admin Only')}</h5>
              <h6>
                {t('If enabled, the field will only be accessible and visible to those with the Admin position.')}
              </h6>
            </div>
          }
        />
      </div>
      <ButtonTray className={'mb-5'}>
        <Button type={'primary'} onClick={form.handleSubmit} className={'mr-2'}>
          {selectedRow ? t('Update') : t('Create')}
        </Button>
        {selectedRow && (
          <Button type={'secondary'} onClick={deleteConfirmationDialog.open}>
            {t('Delete')}
          </Button>
        )}
      </ButtonTray>
    </div>
  );

  const classes = classNames('create-edit-custom-field-dialog', className);

  return (
    <>
      <HeaderDialog className={classes} onClose={onClose} isOpen={isOpen} title={title}>
        <Form
          render={renderFormFields}
          data={formData}
          onSubmit={handleSubmit}
          onChange={(data) => setFormData(data!)}
        />
      </HeaderDialog>
      <DeleteConfirmationDialog
        isOpen={deleteConfirmationDialog.isOpen}
        onClose={deleteConfirmationDialog.close}
        onDelete={handleDelete}
        title={t('Delete Custom Field')}
      >
        {t(
          'This action can not be undone. Any data entered in these fields will be permanently erased. Are you sure you want to continue?'
        )}
      </DeleteConfirmationDialog>
    </>
  );
};

export default CreateEditCustomFieldDialog;
